import { FC, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import { Trans, useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import logo from 'assets/logo-full.svg';

const RETRY_COOLDOWN_SECONDS = 5;

interface ServiceUnavailableProps {
  error?: Error;
}

const ServiceUnavailable: FC<ServiceUnavailableProps> = ({ error }) => {
  const { t } = useTranslation();
  const [cooldown, setCooldown] = useState(RETRY_COOLDOWN_SECONDS);

  useEffect(() => {
    Sentry.captureException(error ?? new Error('Service unavailable page rendered'));
  }, [error]);

  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        px: 3,
        background: 'linear-gradient(135deg, #6358E6 0%, #FC6D43 100%)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 660,
          mt: 4,
          px: 4,
          pt: 7,
          pb: 5,
          textAlign: 'center',
          borderRadius: 4,
          border: '1px solid rgba(255, 255, 255, 0.25)',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.18)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            px: 3.25,
            py: 2.25,
            borderRadius: 999,
            background: 'linear-gradient(135deg, #6358E6 0%, #7B6FF0 100%)',
            boxShadow: '0 8px 20px rgba(99, 88, 230, 0.45)',
          }}
        >
          <Box component="img" src={logo} alt="Booking Plus" sx={{ height: 20 }} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            mx: 'auto',
            mb: 2,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
          }}
        >
          <CloudOffOutlinedIcon sx={{ fontSize: 24, color: 'rgba(0, 0, 0, 0.45)' }} />
        </Box>

        <Typography variant="h6" component="h1" sx={{ fontWeight: 700, color: 'common.white' }}>
          {t('serviceUnavailable.title')}
        </Typography>
        <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-line', color: 'rgba(255, 255, 255, 0.8)' }}>
          <Trans i18nKey={'serviceUnavailable.message'} />
        </Typography>

        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          disabled={cooldown > 0}
          sx={{
            mt: 3,
            textTransform: 'none',
            fontWeight: 'bold',
            backgroundColor: 'common.white',
            color: 'primary.main',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
          }}
        >
          {cooldown > 0 ? t('serviceUnavailable.retryCooldown', { seconds: cooldown }) : t('serviceUnavailable.retry')}
        </Button>
      </Box>
    </Box>
  );
};

export default ServiceUnavailable;
